import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { PwycPriceManager } from "@shared/modules/seating/services/PwycPriceManager";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { useTranslation } from "@shared/utils/internationalization";
import { useCallback, useEffect, useMemo } from "react";
import { enums } from "@shared/constants";
import { SeatingModal } from "../../enums/seating-enums";
import { SeatSelectModalProps, SeatType } from "./SeatSelectModal.props";

const pwycPriceManager = PwycPriceManager.getInstance();

export function useSeatSelectModal(
  props: SeatSelectModalProps & {
    seats: Array<SeatType>;
    updateSeatArray: (inputSeats: Array<SeatType>) => void;
  }
) {
  const {
    onClose,
    resolve,
    seatsToAdd,
    itemGroupParent,
    seats,
    updateSeatArray,
  } = props;

  const { t } = useTranslation("seating");
  const { originalPendingItem, seatIdArray, modalType, originalSeat } =
    seatsToAdd;
  const { seatingServiceState, stageServiceState } = useSeatingState();
  const { paymentType, purchaseSourcePlatform } = useBasketService();

  const isIsolatedSelectionDisabled = useMemo(
    () =>
      Boolean(
        itemGroupParent &&
          modalType === SeatingModal.Isolated &&
          seatingServiceState.maxQuantityRemaining < seatIdArray.length + 1
      ),
    [itemGroupParent, modalType, seatingServiceState]
  );

  const hideDetailCard = isIsolatedSelectionDisabled;

  useEffect(() => {
    if (seatIdArray?.length) {
      const promiseArray = seatIdArray?.map(async (seatId) => {
        // fetch seat data from id array
        const $data = await stageServiceState?.getSeatData(seatId);

        let allowedPermissionsOnSeat = seatingServiceState?.seatingMap[seatId];

        // if the original seat that was clicked on will be added to the pending cart,
        // only show the same item type on the isolated seat select modal
        if (originalPendingItem) {
          allowedPermissionsOnSeat = seatingServiceState?.seatingMap[
            seatId
          ].filter((p) => p.seatingItemTypeId === originalPendingItem);
        }

        const permissions = allowedPermissionsOnSeat?.map((permission) => {
          const seatingItemType =
            seatingServiceState?.itemTypeMap[permission.seatingItemTypeId];

          return {
            ...permission,
            seatingItemType,
          };
        });

        // if there is only 1 item type on the seat, auto-select that item type
        let selectedPermission =
          permissions?.length === 1 ? permissions?.[0] : undefined;

        // if there is a resale id on the seat, auto-select the item type with a resale id
        if (
          modalType === SeatingModal.Resale &&
          permissions?.find((p) => p.seatingItemResaleId)
        ) {
          selectedPermission = permissions?.find((p) => p.seatingItemResaleId);
        }

        // Add PWYC price to the selected permission if it exists
        if (selectedPermission) {
          const itemTypeId = selectedPermission.seatingItemType.id;
          const selectedPwycPrice = pwycPriceManager.getPwycPrice(
            itemTypeId,
            enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id
          );

          if (selectedPwycPrice !== undefined) {
            selectedPermission = {
              ...selectedPermission,
              selectedPwycPrice,
              seatingItemType: {
                ...selectedPermission.seatingItemType,
                price: selectedPwycPrice,
              },
            };
          }
        }

        return {
          seatId,
          $data,
          permissions,
          selectedPermission,
        };
      });

      Promise.all(promiseArray).then((res) => {
        updateSeatArray(res);
      });
    }
  }, [
    modalType,
    seatIdArray,
    seatingServiceState,
    stageServiceState,
    originalPendingItem,
  ]);

  // only auto suggest seats if under the allowable amount remaining
  const seatingMaxQuantityRemaining =
    seatingServiceState?.maxQuantityRemaining ?? -1;

  const autoSelectSeats = useMemo(
    () =>
      seatingMaxQuantityRemaining === -1 ||
      seats.length + 1 <= seatingMaxQuantityRemaining,
    [seatingMaxQuantityRemaining, seats]
  );

  const allSeatsSelected = useMemo(
    () => seats && !seats.find((seat) => !seat.selectedPermission),
    [seats]
  );

  const cancelModal = useCallback(() => {
    onClose(null);
  }, [onClose]);

  const confirmFullPurchase = useCallback(() => {
    resolve(
      seats
        ?.filter((item) => item.seatId === originalSeat.id)
        .map(({ $data, selectedPermission }) => {
          return {
            itemTypeId: selectedPermission?.seatingItemTypeId,
            permissionId: selectedPermission?.seatingItemPermissionId,
            $data,
            itemGroupParent,
            minPurchaseLimit: selectedPermission?.seatingItemMinPurchaseLimit,
            resaleId: selectedPermission?.seatingItemResaleId,
            payWhatYouCanPrice: selectedPermission?.selectedPwycPrice,
          };
        })
    );
  }, [itemGroupParent, resolve, onClose, seats]);

  const confirmSelection = useCallback(() => {
    if (isIsolatedSelectionDisabled) {
      onClose(null);
    }

    resolve(
      seats?.map(({ $data, selectedPermission }) => {
        return {
          itemTypeId: selectedPermission?.seatingItemTypeId,
          permissionId: selectedPermission?.seatingItemPermissionId,
          $data,
          itemGroupParent,
          minPurchaseLimit: selectedPermission?.seatingItemMinPurchaseLimit,
          resaleId: selectedPermission?.seatingItemResaleId,
          payWhatYouCanPrice: selectedPermission?.selectedPwycPrice,
        };
      })
    );
  }, [itemGroupParent, resolve, onClose, seats, isIsolatedSelectionDisabled]);

  /**
   * Returns alert content (title, subtitle, status) for the current modal type.
   * Used by SeatActionRequiredAlert to render the Alert banner.
   */
  const getDescriptionText = useCallback(() => {
    let title: string;
    let subtitle: string;
    let status: "error" | "warning" | "success" | "info" = "warning";

    const getIsolatedSubtitle = (): string => {
      if (autoSelectSeats) {
        return t("Clicking continue will also add the following").concat(":");
      }
      return t("Please select a different seat.");
    };

    switch (modalType) {
      case SeatingModal.Isolated:
        status = isIsolatedSelectionDisabled ? "error" : "warning";
        title = isIsolatedSelectionDisabled
          ? t("Unable to add selection")
          : t("Additional seat selection required");
        subtitle = isIsolatedSelectionDisabled
          ? t(
              "You are about to leave an isolated seat in the row and you don't have enough selections remaining to add both seats."
            )
          : `${t(
              "Selecting this seat would leave an empty seat at the end of a row or between two others."
            )}\n\n${getIsolatedSubtitle()}`;
        break;
      case SeatingModal.FullPurchase:
        title = t("Full table selection required");
        subtitle = `${t(
          "This seat is part of a table that requires all seats to be purchased at once."
        )}\n\n${t("Clicking continue will also add the following").concat(
          ":"
        )}`;
        break;
      case SeatingModal.Resale:
        title = t("Showpass official resale");
        subtitle = `${t(
          "This seat is part of a resale group that requires all seats to be purchased at once."
        )}\n\n${t("Clicking continue will add the following").concat(":")}`;
        break;
      default:
        title = "";
        subtitle = "";
    }

    return { title, subtitle, status };
  }, [modalType, isIsolatedSelectionDisabled, autoSelectSeats, t]);

  return {
    isIsolatedSelectionDisabled,
    paymentType,
    purchaseSourcePlatform,
    hideDetailCard,
    autoSelectSeats,
    allSeatsSelected,
    cancelModal,
    confirmFullPurchase: SeatingModal.FullPurchase ? confirmFullPurchase : null,
    confirmSelection,
    getDescriptionText,
  };
}
